import { LinkRecordSchema, ObjectRecordSchema, type Id, type LinkRecord, type ObjectRecord, type Transaction } from '@vp/shared-types';
import { boxBounds, type Rect } from '../geometry';
import type { CanvasMetaExt, LoadOptions, LoadResult, LogQuery, ModuleDataStore, ModuleStore, SaveBatch, StorageAdapter, StorageUsage } from './adapter';
import type { LogRow, ModuleDataRow } from './db';
import { attachmentRefs, sha256Hex } from './dexie-adapter';
import { copyCanvasParts } from '../canvas-parts';

/*
 * Canvases in a folder instead of IndexedDB: a folder the user picked (File System Access API) or the
 * origin private file system — anything with the directory-handle interface below. The files are plain
 * JSON, readable and copyable by hand:
 *
 *   vp-storage.json                    format marker {format, version, createdAt, profile} — profile: its owner (docs/TZ-PROFILES.md)
 *   canvases/<id>/meta.json            canvas metadata (name, camera, settings, appliedSeq…)
 *   canvases/<id>/objects/NN.json      objects, in 16 shards by id (a save rewrites only the shards it touches)
 *   canvases/<id>/links.json           links
 *   canvases/<id>/wal/<seq>.json       operation log written before the state (§10.3); removed at the checkpoint
 *   canvases/<id>/module-data.json     modules' data of this canvas (API 2.2)
 *   canvases/<id>/preview.png          the preview on "My canvases"
 *   canvases/<id>/quarantine.json      records that failed validation (§10.4), kept, never loaded
 *   attachments/<sha256>               files (images…) by content hash; attachments/index.json — their types
 *
 * What belongs to the browser installation rather than to the canvases — app settings, the journal,
 * installed modules and modules' app-wide data — stays in the `base` adapter (IndexedDB).
 *
 * A save writes the objects and links first and the metadata with the checkpoint last: a crash in
 * between leaves the WAL entries after the old checkpoint, and they are replayed on the next load.
 * Each file is replaced atomically by the API (written to a temporary file, swapped on close).
 */

export const FOLDER_FORMAT = 'vp-folder';
export const FOLDER_FORMAT_VERSION = 1;
export const MARKER_FILE = 'vp-storage.json';
/** A folder of several profiles: each one's data is in the subfolder named by its id. */
export const SET_MARKER_FILE = 'vp-profiles.json';
export const FOLDER_SET_FORMAT = 'vp-folder-set';
const SHARDS = 16;

export interface FsWritable {
  write(data: string | Blob | ArrayBuffer | Uint8Array): Promise<void>;
  close(): Promise<void>;
}
export interface FsFileHandle {
  readonly kind: 'file';
  readonly name: string;
  getFile(): Promise<Blob & { lastModified: number }>;
  createWritable(): Promise<FsWritable>;
}
export interface FsDirHandle {
  readonly kind: 'directory';
  readonly name: string;
  getDirectoryHandle(name: string, opts?: { create?: boolean }): Promise<FsDirHandle>;
  getFileHandle(name: string, opts?: { create?: boolean }): Promise<FsFileHandle>;
  removeEntry(name: string, opts?: { recursive?: boolean }): Promise<void>;
  entries(): AsyncIterableIterator<[string, FsDirHandle | FsFileHandle]>;
}

const isNotFound = (e: unknown) => (e as { name?: string } | null)?.name === 'NotFoundError' || (e as { name?: string } | null)?.name === 'TypeMismatchError';

/**
 * What the folder holds: one profile's data (`profile` — its owner; none in folders made before
 * profiles), a folder of profiles (`set`), nothing, or someone else's files.
 */
export type FolderState = { kind: 'ours'; version: number; profile?: string } | { kind: 'set' } | { kind: 'empty' } | { kind: 'other' };

/** The folder is one profile's and another profile tried to open it. */
export class FolderOwnedError extends Error {
  constructor(readonly owner: string) {
    super('this folder belongs to another profile');
  }
}

async function readMarker(dir: FsDirHandle, name: string): Promise<{ format?: string; version?: number; profile?: unknown } | null> {
  try {
    const f = await (await dir.getFileHandle(name)).getFile();
    return JSON.parse(await f.text()) as { format?: string; version?: number; profile?: unknown };
  } catch {
    return null;
  }
}

export async function inspectFolder(dir: FsDirHandle): Promise<FolderState> {
  const set = await readMarker(dir, SET_MARKER_FILE);
  if (set?.format === FOLDER_SET_FORMAT) return { kind: 'set' };
  const m = await readMarker(dir, MARKER_FILE);
  if (m?.format === FOLDER_FORMAT) return { kind: 'ours', version: m.version ?? 1, ...(typeof m.profile === 'string' && m.profile ? { profile: m.profile } : {}) };
  for await (const [name] of dir.entries()) if (!name.startsWith('.')) return { kind: 'other' };
  return { kind: 'empty' };
}

/** Which shard an object id lives in (stable: the shard does not depend on the object's position). */
export function shardOf(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return (h >>> 0) % SHARDS;
}
const shardName = (n: number) => String(n).padStart(2, '0') + '.json';
const walName = (seq: number) => String(seq).padStart(12, '0') + '.json';

function intersects(o: ObjectRecord, r: Rect): boolean {
  const b = boxBounds(o);
  return b.x <= r.x + r.w && b.x + b.w >= r.x && b.y <= r.y + r.h && b.y + b.h >= r.y;
}

interface Shard {
  rows: Map<Id, ObjectRecord>;
  /** The file's time when it was read or written: another writer (a tab, a sync tool) changes it. */
  stamp: number;
}

export class FolderStorage implements StorageAdapter {
  /** Shards of the canvas saved last (the one being edited), so a save does not re-read them. */
  private cache: { canvasId: Id; shards: Map<number, Shard> } | null = null;
  private seqs = new Map<Id, number>();
  private writes: Promise<unknown> = Promise.resolve();

  constructor(
    readonly root: FsDirHandle,
    /** App settings, journal, modules, modules' app-wide data. */
    private readonly base: StorageAdapter,
  ) {}

  /**
   * Mark a folder as ours (a new storage); an existing marker is kept. `owner`: the profile it belongs
   * to — a folder of another profile is refused, one made before profiles is taken over.
   */
  async init(owner?: string): Promise<void> {
    const st = await inspectFolder(this.root);
    if (st.kind === 'ours') {
      if (st.version > FOLDER_FORMAT_VERSION) throw new Error(`folder storage format ${st.version} is newer than this app understands (${FOLDER_FORMAT_VERSION})`);
      if (owner && st.profile && st.profile !== owner) throw new FolderOwnedError(st.profile);
      if (!owner || st.profile === owner) return;
      const m = (await readMarker(this.root, MARKER_FILE)) ?? {};
      await this.writeText(this.root, MARKER_FILE, JSON.stringify({ ...m, profile: owner }, null, 2));
      return;
    }
    if (st.kind === 'set') throw new Error('this is a folder of profiles: open a profile in its subfolder');
    await this.writeText(this.root, MARKER_FILE, JSON.stringify({ format: FOLDER_FORMAT, version: FOLDER_FORMAT_VERSION, createdAt: new Date().toISOString(), ...(owner ? { profile: owner } : {}) }, null, 2));
  }

  /** Write, read and delete a file: permission and the folder itself are checked before anything else. */
  async probe(): Promise<void> {
    await this.writeText(this.root, '.vp-probe', String(Date.now()));
    await this.readText(this.root, '.vp-probe');
    await this.root.removeEntry('.vp-probe');
  }

  // ------------------------------------------------------------------ file helpers

  /** Writes go one after another: two saves never interleave on the same file. */
  private serial<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.writes.then(fn, fn);
    this.writes = run.catch(() => undefined);
    return run;
  }

  private async dir(path: string[], create: boolean): Promise<FsDirHandle | null> {
    let d = this.root;
    try {
      for (const p of path) d = await d.getDirectoryHandle(p, { create });
      return d;
    } catch (e) {
      if (!create && isNotFound(e)) return null;
      throw e;
    }
  }

  private canvasDir(id: Id, create = false): Promise<FsDirHandle | null> {
    return this.dir(['canvases', id], create);
  }

  private async readFile(d: FsDirHandle, name: string): Promise<(Blob & { lastModified: number }) | null> {
    try {
      return await (await d.getFileHandle(name)).getFile();
    } catch (e) {
      if (isNotFound(e)) return null;
      throw e;
    }
  }

  private async readText(d: FsDirHandle, name: string): Promise<string | null> {
    const f = await this.readFile(d, name);
    return f ? f.text() : null;
  }

  private async readJson<T>(d: FsDirHandle | null, name: string): Promise<T | null> {
    if (!d) return null;
    const s = await this.readText(d, name);
    if (s === null) return null;
    try {
      return JSON.parse(s) as T;
    } catch {
      // a damaged file is set aside, never overwritten silently
      await this.writeText(d, name + '.damaged-' + Date.now(), s);
      return null;
    }
  }

  private async writeText(d: FsDirHandle, name: string, data: string | Blob): Promise<void> {
    const w = await (await d.getFileHandle(name, { create: true })).createWritable();
    await w.write(data);
    await w.close();
  }

  private async remove(d: FsDirHandle | null, name: string, recursive = false): Promise<void> {
    if (!d) return;
    try {
      await d.removeEntry(name, { recursive });
    } catch (e) {
      if (!isNotFound(e)) throw e;
    }
  }

  private async names(d: FsDirHandle | null): Promise<string[]> {
    const out: string[] = [];
    if (!d) return out;
    for await (const [name] of d.entries()) out.push(name);
    return out;
  }

  // ------------------------------------------------------------------ canvases

  async listCanvases(): Promise<CanvasMetaExt[]> {
    const all = await this.dir(['canvases'], false);
    const out: CanvasMetaExt[] = [];
    for (const id of await this.names(all)) {
      const m = await this.getCanvas(id);
      if (m) out.push(m);
    }
    return out.sort((a, b) => b.updatedAt - a.updatedAt);
  }

  async getCanvas(id: Id): Promise<CanvasMetaExt | undefined> {
    return (await this.readJson<CanvasMetaExt>(await this.canvasDir(id), 'meta.json')) ?? undefined;
  }

  putCanvas(meta: CanvasMetaExt): Promise<void> {
    return this.serial(async () => this.writeText((await this.canvasDir(meta.id, true))!, 'meta.json', JSON.stringify(meta)));
  }

  updateCanvas(id: Id, patch: Partial<CanvasMetaExt>): Promise<void> {
    return this.serial(async () => {
      const d = await this.canvasDir(id);
      const m = await this.readJson<CanvasMetaExt>(d, 'meta.json');
      if (d && m) await this.writeText(d, 'meta.json', JSON.stringify({ ...m, ...patch }));
    });
  }

  deleteCanvas(id: Id): Promise<void> {
    return this.serial(async () => {
      if (this.cache?.canvasId === id) this.cache = null;
      this.seqs.delete(id);
      await this.remove(await this.dir(['canvases'], false), id, true);
    });
  }

  private async readShard(d: FsDirHandle | null, n: number): Promise<{ list: unknown[]; stamp: number }> {
    const f = d ? await this.readFile(d, shardName(n)) : null;
    if (!f) return { list: [], stamp: 0 };
    try {
      const v = JSON.parse(await f.text()) as unknown;
      return { list: Array.isArray(v) ? v : [], stamp: f.lastModified };
    } catch {
      await this.writeText(d!, shardName(n) + '.damaged-' + Date.now(), await f.text());
      return { list: [], stamp: 0 };
    }
  }

  async loadCanvas(id: Id, opts: LoadOptions = {}): Promise<LoadResult> {
    const d = await this.canvasDir(id);
    const meta = await this.readJson<CanvasMetaExt>(d, 'meta.json');
    if (!d || !meta) throw new Error(`Canvas ${id} not found`);
    const objDir = await d.getDirectoryHandle('objects', { create: true });
    const shards = new Map<number, Shard>();
    const bad: { table: string; reason: string; data: unknown }[] = [];
    const all: ObjectRecord[] = [];
    for (let n = 0; n < SHARDS; n++) {
      const { list, stamp } = await this.readShard(objDir, n);
      const rows = new Map<Id, ObjectRecord>();
      let dropped = false;
      for (const r of list) {
        const res = ObjectRecordSchema.safeParse(r);
        const rec = r as ObjectRecord;
        if (res.success && rec.canvasId === id && !rows.has(rec.id)) {
          rows.set(rec.id, rec);
          all.push(rec);
        } else {
          bad.push({ table: 'objects', reason: res.success ? 'canvas mismatch' : (res.error.issues[0]?.message ?? 'invalid'), data: r });
          dropped = true;
        }
      }
      shards.set(n, { rows, stamp: dropped ? -1 : stamp });
    }
    const links: LinkRecord[] = [];
    const goodLinks: LinkRecord[] = [];
    for (const l of (await this.readJson<unknown[]>(d, 'links.json')) ?? []) {
      const res = LinkRecordSchema.safeParse(l);
      if (res.success && (l as LinkRecord).canvasId === id) goodLinks.push(l as LinkRecord);
      else bad.push({ table: 'links', reason: res.success ? 'canvas mismatch' : (res.error.issues[0]?.message ?? 'invalid'), data: l });
    }
    links.push(...goodLinks);

    // move corrupted records aside without deleting them (§10.4)
    if (bad.length) {
      await this.serial(async () => {
        const q = (await this.readJson<unknown[]>(d, 'quarantine.json')) ?? [];
        q.push(...bad.map((b) => ({ ...b, ts: Date.now() })));
        await this.writeText(d, 'quarantine.json', JSON.stringify(q));
        for (const [n, s] of shards) {
          if (s.stamp !== -1) continue;
          await this.writeText(objDir, shardName(n), JSON.stringify([...s.rows.values()]));
          s.stamp = (await this.readFile(objDir, shardName(n)))?.lastModified ?? 0;
        }
        if (bad.some((b) => b.table === 'links')) await this.writeText(d, 'links.json', JSON.stringify(goodLinks));
      });
    }
    this.cache = { canvasId: id, shards };

    const chunk = opts.chunkSize ?? 5000;
    const emit = async (list: ObjectRecord[], stage: 'visible' | 'rest') => {
      for (let i = 0; i < list.length; i += chunk) {
        opts.onChunk?.(list.slice(i, i + chunk), stage);
        await new Promise((r) => setTimeout(r, 0));
      }
    };
    if (opts.view) {
      const view = opts.view;
      await emit(
        all.filter((o) => intersects(o, view)),
        'visible',
      );
      await emit(
        all.filter((o) => !intersects(o, view)),
        'rest',
      );
    } else await emit(all, 'rest');

    // the operation log after the checkpoint is replayed by the caller; entries up to it are leftovers
    const applied = meta.appliedSeq ?? 0;
    const walDir = await this.dir(['canvases', id, 'wal'], false);
    const replay: Transaction[] = [];
    let maxSeq = applied;
    for (const name of (await this.names(walDir)).sort()) {
      const seq = Number.parseInt(name, 10);
      if (!Number.isFinite(seq)) continue;
      maxSeq = Math.max(maxSeq, seq);
      if (seq <= applied) continue;
      const txs = await this.readJson<Transaction[]>(walDir, name);
      if (txs) replay.push(...txs);
    }
    this.seqs.set(id, maxSeq);
    return { meta, links, replay, quarantined: bad.length, objectCount: all.length };
  }

  createCanvasWithContent(meta: CanvasMetaExt, objects: ObjectRecord[], links: LinkRecord[]): Promise<void> {
    return this.serial(async () => {
      const all = (await this.dir(['canvases'], true))!;
      if ((await this.names(all)).includes(meta.id) && (await this.getCanvas(meta.id))) throw new Error(`Canvas ${meta.id} already exists`);
      const d = (await this.canvasDir(meta.id, true))!;
      const objDir = await d.getDirectoryHandle('objects', { create: true });
      const shards: ObjectRecord[][] = Array.from({ length: SHARDS }, () => []);
      for (const o of objects) shards[shardOf(o.id)].push(o);
      for (let n = 0; n < SHARDS; n++) if (shards[n].length) await this.writeText(objDir, shardName(n), JSON.stringify(shards[n]));
      if (links.length) await this.writeText(d, 'links.json', JSON.stringify(links));
      // the metadata last: a canvas without it is not listed (an interrupted import leaves nothing visible)
      await this.writeText(d, 'meta.json', JSON.stringify({ ...meta, objectCount: objects.length }));
    });
  }

  async readCanvasContent(id: Id): Promise<{ objects: ObjectRecord[]; links: LinkRecord[] }> {
    const d = await this.canvasDir(id);
    const objDir = d ? await this.dir(['canvases', id, 'objects'], false) : null;
    const objects: ObjectRecord[] = [];
    for (let n = 0; n < SHARDS; n++) objects.push(...((await this.readShard(objDir, n)).list as ObjectRecord[]));
    return { objects, links: (await this.readJson<LinkRecord[]>(d, 'links.json')) ?? [] };
  }

  // ------------------------------------------------------------------ saving

  appendOps(canvasId: Id, txs: Transaction[]): Promise<number> {
    if (!txs.length) return Promise.resolve(0);
    return this.serial(async () => {
      const walDir = (await this.dir(['canvases', canvasId, 'wal'], true))!;
      let seq = this.seqs.get(canvasId);
      if (seq === undefined) {
        // first write to this canvas in this tab: continue after what is there
        const meta = await this.getCanvas(canvasId);
        seq = meta?.appliedSeq ?? 0;
        for (const name of await this.names(walDir)) seq = Math.max(seq, Number.parseInt(name, 10) || 0);
      }
      seq += txs.length;
      await this.writeText(walDir, walName(seq), JSON.stringify(txs));
      this.seqs.set(canvasId, seq);
      return seq;
    });
  }

  private async shardFor(canvasId: Id, objDir: FsDirHandle, n: number): Promise<Shard> {
    if (this.cache?.canvasId !== canvasId) this.cache = { canvasId, shards: new Map() };
    const have = this.cache.shards.get(n);
    const f = await this.readFile(objDir, shardName(n));
    // still the file we read or wrote: no need to read it again
    if (have && (f?.lastModified ?? 0) === have.stamp) return have;
    const { list } = await this.readShard(objDir, n);
    const s: Shard = { rows: new Map((list as ObjectRecord[]).map((o) => [o.id, o])), stamp: f?.lastModified ?? 0 };
    this.cache.shards.set(n, s);
    return s;
  }

  saveBatch(b: SaveBatch): Promise<void> {
    return this.serial(async () => {
      const d = await this.canvasDir(b.canvasId);
      const meta = await this.readJson<CanvasMetaExt>(d, 'meta.json');
      if (!d || !meta) return; // deleted meanwhile (Dexie's update of a missing row does nothing either)
      if (b.putObjects.length || b.deleteObjects.length) {
        const objDir = await d.getDirectoryHandle('objects', { create: true });
        const touched = new Set<number>();
        for (const o of b.putObjects) touched.add(shardOf(o.id));
        for (const id of b.deleteObjects) touched.add(shardOf(id));
        for (const n of touched) {
          const s = await this.shardFor(b.canvasId, objDir, n);
          for (const o of b.putObjects) if (shardOf(o.id) === n) s.rows.set(o.id, o);
          for (const id of b.deleteObjects) if (shardOf(id) === n) s.rows.delete(id);
          if (s.rows.size) await this.writeText(objDir, shardName(n), JSON.stringify([...s.rows.values()]));
          else await this.remove(objDir, shardName(n));
          s.stamp = (await this.readFile(objDir, shardName(n)))?.lastModified ?? 0;
        }
      }
      if (b.putLinks.length || b.deleteLinks.length) {
        const links = new Map(((await this.readJson<LinkRecord[]>(d, 'links.json')) ?? []).map((l) => [l.id, l]));
        for (const l of b.putLinks) links.set(l.id, l);
        for (const id of b.deleteLinks) links.delete(id);
        await this.writeText(d, 'links.json', JSON.stringify([...links.values()]));
      }
      const next: CanvasMetaExt = { ...meta, ...(b.meta ?? {}), updatedAt: Date.now() };
      if (b.appliedSeq !== undefined) next.appliedSeq = b.appliedSeq;
      await this.writeText(d, 'meta.json', JSON.stringify(next));
      // the checkpoint is written: the log up to it is not needed any more
      if (b.appliedSeq !== undefined) {
        const walDir = await this.dir(['canvases', b.canvasId, 'wal'], false);
        for (const name of await this.names(walDir)) if ((Number.parseInt(name, 10) || 0) <= b.appliedSeq) await this.remove(walDir, name);
      }
    });
  }

  pruneOplog(canvasId: Id): Promise<void> {
    // saveBatch already drops the log up to the checkpoint; nothing older is kept
    void canvasId;
    return Promise.resolve();
  }

  // ------------------------------------------------------------------ attachments

  private async attachmentTypes(): Promise<Record<string, string>> {
    return (await this.readJson<Record<string, string>>(await this.dir(['attachments'], false), 'index.json')) ?? {};
  }

  async putAttachment(blob: Blob, mime?: string): Promise<string> {
    const data = await blob.arrayBuffer();
    const hash = await sha256Hex(data);
    return this.serial(async () => {
      const d = (await this.dir(['attachments'], true))!;
      if (!(await this.readFile(d, hash))) await this.writeText(d, hash, new Blob([data]));
      const types = await this.attachmentTypes();
      const type = mime ?? blob.type;
      if (types[hash] !== type) {
        types[hash] = type;
        await this.writeText(d, 'index.json', JSON.stringify(types));
      }
      return hash;
    });
  }

  async getAttachment(hash: string): Promise<Blob | undefined> {
    if (!/^[0-9a-f]{64}$/.test(hash)) return undefined;
    const d = await this.dir(['attachments'], false);
    const f = d ? await this.readFile(d, hash) : null;
    if (!f) return undefined;
    const type = (await this.attachmentTypes())[hash] ?? '';
    return new Blob([await f.arrayBuffer()], { type });
  }

  async hasAttachment(hash: string): Promise<boolean> {
    if (!/^[0-9a-f]{64}$/.test(hash)) return false;
    const d = await this.dir(['attachments'], false);
    return !!d && !!(await this.readFile(d, hash));
  }

  private async referenced(): Promise<{ all: Set<string>; per: Map<Id, { bytes: number; refs: Set<string> }> }> {
    const all = new Set<string>();
    const per = new Map<Id, { bytes: number; refs: Set<string> }>();
    const canvases = await this.dir(['canvases'], false);
    for (const id of await this.names(canvases)) {
      const c = { bytes: 0, refs: new Set<string>() };
      per.set(id, c);
      const d = await this.canvasDir(id);
      if (!d) continue;
      for (const name of await this.names(d)) {
        if (name === 'preview.png') continue;
        if (name === 'objects' || name === 'wal') {
          const sub = await this.dir(['canvases', id, name], false);
          for (const f of await this.names(sub)) {
            const file = await this.readFile(sub!, f);
            if (!file) continue;
            c.bytes += file.size;
            if (name === 'objects') {
              try {
                for (const o of JSON.parse(await file.text()) as ObjectRecord[]) for (const h of attachmentRefs(o)) c.refs.add(h);
              } catch {
                /* damaged shard: set aside on load */
              }
            }
          }
        } else c.bytes += (await this.readFile(d, name).catch(() => null))?.size ?? 0;
      }
      for (const h of c.refs) all.add(h);
    }
    return { all, per };
  }

  gcAttachments(minAgeMs = 60 * 60 * 1000): Promise<number> {
    return this.serial(async () => {
      const d = await this.dir(['attachments'], false);
      if (!d) return 0;
      const { all } = await this.referenced();
      const types = await this.attachmentTypes();
      const now = Date.now();
      let n = 0;
      for (const name of await this.names(d)) {
        if (!/^[0-9a-f]{64}$/.test(name) || all.has(name)) continue;
        const f = await this.readFile(d, name);
        if (!f || now - f.lastModified <= minAgeMs) continue;
        await this.remove(d, name);
        delete types[name];
        n++;
      }
      if (n) await this.writeText(d, 'index.json', JSON.stringify(types));
      return n;
    });
  }

  // ------------------------------------------------------------------ settings: previews live with their canvas

  async getSetting<T>(key: string): Promise<T | undefined> {
    if (key.startsWith('thumb:')) {
      const d = await this.canvasDir(key.slice(6));
      const f = d ? await this.readFile(d, 'preview.png') : null;
      return (f ? new Blob([await f.arrayBuffer()], { type: f.type || 'image/png' }) : undefined) as T | undefined;
    }
    return this.base.getSetting<T>(key);
  }

  async setSetting(key: string, value: unknown): Promise<void> {
    if (key.startsWith('thumb:')) {
      const id = key.slice(6);
      return this.serial(async () => {
        const d = await this.canvasDir(id);
        if (!d) return;
        if (value instanceof Blob) await this.writeText(d, 'preview.png', value);
        else await this.remove(d, 'preview.png');
      });
    }
    return this.base.setSetting(key, value);
  }

  // ------------------------------------------------------------------ the browser's part

  appendLogs(rows: LogRow[]): Promise<void> {
    return this.base.appendLogs(rows);
  }
  queryLogs(q?: LogQuery): Promise<LogRow[]> {
    return this.base.queryLogs(q);
  }
  pruneLogs(olderThan: number): Promise<number> {
    return this.base.pruneLogs(olderThan);
  }
  clearLogs(): Promise<void> {
    return this.base.clearLogs();
  }
  get modules(): ModuleStore {
    return this.base.modules;
  }

  /** A canvas's modules' data travels with the canvas folder; `app` scope stays in the browser. */
  readonly moduleData: ModuleDataStore = {
    get: async (moduleId, scope, key) => {
      if (scope === 'app') return this.base.moduleData.get(moduleId, scope, key);
      return (await this.canvasData(scope)).find((r) => r.moduleId === moduleId && r.key === key);
    },
    put: (row) => {
      if (row.scope === 'app') return this.base.moduleData.put(row);
      return this.editCanvasData(row.scope, (rows) => [...rows.filter((r) => !(r.moduleId === row.moduleId && r.key === row.key)), row]);
    },
    delete: (moduleId, scope, key) => {
      if (scope === 'app') return this.base.moduleData.delete(moduleId, scope, key);
      return this.editCanvasData(scope, (rows) => rows.filter((r) => !(r.moduleId === moduleId && r.key === key)));
    },
    list: async (moduleId, scope) => {
      if (scope === 'app') return this.base.moduleData.list(moduleId, scope);
      return (await this.canvasData(scope)).filter((r) => r.moduleId === moduleId);
    },
    ofScope: async (scope) => (scope === 'app' ? this.base.moduleData.ofScope(scope) : this.canvasData(scope)),
    deleteScope: (moduleId, scope) => {
      if (scope === 'app') return this.base.moduleData.deleteScope(moduleId, scope);
      return this.editCanvasData(scope, (rows) => rows.filter((r) => r.moduleId !== moduleId));
    },
  };

  private async canvasData(canvasId: string): Promise<ModuleDataRow[]> {
    return (await this.readJson<ModuleDataRow[]>(await this.canvasDir(canvasId), 'module-data.json')) ?? [];
  }

  private editCanvasData(canvasId: string, fn: (rows: ModuleDataRow[]) => ModuleDataRow[]): Promise<void> {
    return this.serial(async () => {
      const d = (await this.canvasDir(canvasId, true))!;
      const rows = fn((await this.readJson<ModuleDataRow[]>(d, 'module-data.json')) ?? []);
      if (rows.length) await this.writeText(d, 'module-data.json', JSON.stringify(rows));
      else await this.remove(d, 'module-data.json');
    });
  }

  // ------------------------------------------------------------------ space

  /** A folder has no browser quota: the disk's free space is not something a page can see. */
  estimate(): Promise<{ usage?: number; quota?: number }> {
    return Promise.resolve({});
  }

  async usage(): Promise<StorageUsage> {
    const { per } = await this.referenced();
    const sizes = new Map<string, number>();
    const att = await this.dir(['attachments'], false);
    for (const name of await this.names(att)) {
      if (!/^[0-9a-f]{64}$/.test(name)) continue;
      sizes.set(name, (await this.readFile(att!, name))?.size ?? 0);
    }
    const referenced = new Set<string>();
    for (const c of per.values()) for (const h of c.refs) referenced.add(h);
    let unused = 0;
    let unusedBytes = 0;
    for (const [h, s] of sizes) {
      if (referenced.has(h)) continue;
      unused++;
      unusedBytes += s;
    }
    let previewBytes = 0;
    for (const id of per.keys()) {
      const d = await this.canvasDir(id);
      previewBytes += (d ? await this.readFile(d, 'preview.png') : null)?.size ?? 0;
    }
    const b = await this.base.usage();
    const canvases = [...per].map(([id, c]) => ({ id, bytes: c.bytes, attachments: [...c.refs].reduce((s, h) => s + (sizes.get(h) ?? 0), 0) }));
    return {
      canvases,
      canvasBytes: canvases.reduce((s, c) => s + c.bytes, 0),
      attachments: { count: sizes.size, bytes: [...sizes.values()].reduce((s, v) => s + v, 0), unused, unusedBytes },
      previewBytes,
      logBytes: b.logBytes,
      moduleBytes: b.moduleBytes,
    };
  }

  clearCanvases(): Promise<void> {
    return this.serial(async () => {
      this.cache = null;
      this.seqs.clear();
      await this.remove(this.root, 'canvases', true);
      await this.remove(this.root, 'attachments', true);
    });
  }

  clearPreviews(): Promise<void> {
    return this.serial(async () => {
      for (const id of await this.names(await this.dir(['canvases'], false))) await this.remove(await this.canvasDir(id), 'preview.png');
    });
  }
}

async function writeFile(dir: FsDirHandle, name: string, text: string): Promise<void> {
  const w = await (await dir.getFileHandle(name, { create: true })).createWritable();
  await w.write(text);
  await w.close();
}

/**
 * A profile's data folder inside a folder of profiles (the subfolder named by its id), marking the
 * outer folder as a folder of profiles if it is not one yet. Refused for a folder of one profile.
 */
export async function profileFolderIn(set: FsDirHandle, profileId: string): Promise<FsDirHandle> {
  const st = await inspectFolder(set);
  if (st.kind === 'ours') throw new FolderOwnedError(st.profile ?? '');
  if (st.kind !== 'set') await writeFile(set, SET_MARKER_FILE, JSON.stringify({ format: FOLDER_SET_FORMAT, version: 1, createdAt: new Date().toISOString() }, null, 2));
  return set.getDirectoryHandle(profileId, { create: true });
}

/** What the platform wrote into a profile's own folder; the folder itself and other files stay. */
export const PLATFORM_ENTRIES = [MARKER_FILE, 'canvases', 'attachments', '.vp-probe'];

/** Remove a profile's data from its folder (the profile is deleted); returns how many entries went. */
export async function removeFolderData(dir: FsDirHandle): Promise<number> {
  let n = 0;
  for (const name of PLATFORM_ENTRIES) {
    try {
      await dir.removeEntry(name, { recursive: true });
      n++;
    } catch (e) {
      if (!isNotFound(e)) throw e;
    }
  }
  return n;
}

/**
 * Copy every canvas (with its files, modules' data and preview) from one storage to another: moving
 * the browser's canvases into a folder. Canvases the target already has are skipped; returns how many
 * were copied.
 */
export async function copyCanvases(from: StorageAdapter, to: StorageAdapter, onProgress?: (done: number, total: number) => void): Promise<number> {
  const list = await from.listCanvases();
  const have = new Set((await to.listCanvases()).map((m) => m.id));
  let done = 0;
  let copied = 0;
  for (const meta of list) {
    if (!have.has(meta.id)) {
      const { objects, links } = await from.readCanvasContent(meta.id);
      for (const o of objects) {
        for (const h of attachmentRefs(o)) {
          if (await to.hasAttachment(h)) continue;
          const blob = await from.getAttachment(h);
          if (blob) await to.putAttachment(blob, blob.type);
        }
      }
      // the copy has the state without a pending log: its checkpoint starts over
      const { appliedSeq: _seq, ...rest } = meta;
      void _seq;
      await to.createCanvasWithContent(rest, objects, links);
      await copyCanvasParts(from, meta.id, to, meta.id);
      const thumb = await from.getSetting<Blob>('thumb:' + meta.id);
      if (thumb instanceof Blob) await to.setSetting('thumb:' + meta.id, thumb);
      copied++;
    }
    onProgress?.(++done, list.length);
  }
  return copied;
}
